import type { MyUnitProfile } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { LEVEL_LABEL, expiryState, formatDate } from '../lib/unit.js';
import { QualChip } from './QualChip.js';

/** Profile: the member's own level, qualifications and positions (admins edit these). */
export function MyUnitCard({ unit }: { unit: MyUnitProfile }) {
  const tags = [
    unit.level ? LEVEL_LABEL[unit.level] : null,
    unit.operational ? 'Operational' : 'Office',
    unit.udo ? 'UDO' : null,
  ].filter(Boolean) as string[];
  const expiring = unit.qualifications.filter((q) => ['soon', 'expired'].includes(expiryState(q.expiresAt)));

  return (
    <div style={{
      margin: '12px 16px', padding: '12px 14px', borderRadius: 12,
      background: theme.surface, border: `1px solid ${theme.border}`, fontFamily: theme.font,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 8 }}>Unit details</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {tags.map((t) => (
          <span key={t} style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
            background: theme.status.service.bg, color: theme.status.service.fg,
          }}>{t}</span>
        ))}
      </div>

      <div style={label}>Qualifications</div>
      {unit.qualifications.length === 0
        ? <div style={muted}>None recorded yet.</div>
        : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {unit.qualifications.map((q) => (
              <QualChip key={q.typeId} code={q.code} grade={q.grade} expiresAt={q.expiresAt} title={q.name} />
            ))}
          </div>
        )}
      {expiring.map((q) => (
        <div key={q.typeId} style={{ fontSize: 12, color: expiryState(q.expiresAt) === 'expired' ? theme.status.danger.fg : theme.status.warn.fg }}>
          {q.code} {expiryState(q.expiresAt) === 'expired' ? 'expired' : 'expires'} {formatDate(q.expiresAt!)}
        </div>
      ))}

      {unit.positions.length > 0 && (
        <>
          <div style={{ ...label, marginTop: 10 }}>Positions</div>
          <div style={{ fontSize: 13, color: theme.text }}>{unit.positions.join(' · ')}</div>
        </>
      )}
      <div style={{ ...muted, marginTop: 10 }}>Something wrong or out of date? Ask an admin to update it.</div>
    </div>
  );
}

const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 6,
};
const muted: React.CSSProperties = { fontSize: 12, color: theme.textMuted };
